import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { ShoppingCart, Trash2, ArrowLeft, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerCart() {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    if (!token) {
      navigate('/customer/login');
      return;
    }
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.get(`${API}/customer/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (productId) => {
    try {
      const token = localStorage.getItem('customer_token');
      await axios.delete(`${API}/customer/cart/remove/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item removed from cart');
      fetchCart();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const calculateTotal = () => {
    return cart.items?.reduce((sum, item) => sum + item.total_price, 0) || 0;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/himan-logo.png" alt="Himan Logo" className="h-12 w-12" />
            <h1 className="text-xl font-bold">Shopping Cart</h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/customer/products')}>
            <ArrowLeft size={16} className="mr-2" />
            Continue Shopping
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {!cart.items || cart.items.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ShoppingCart size={64} className="mx-auto text-gray-400 mb-4" />
              <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
              <p className="text-gray-600 mb-6">Add some products to get started</p>
              <Button onClick={() => navigate('/customer/products')}>
                Browse Products
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4">Cart Items ({cart.items.length})</h2>
                <div className="space-y-4">
                  {cart.items.map((item) => (
                    <div
                      key={item.product_id}
                      data-testid={`cart-item-${item.product_id}`}
                      className="flex items-center justify-between border-b pb-4"
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{item.product_name}</h3>
                        <p className="text-sm text-gray-600">Category: {item.product_category}</p>
                        <p className="text-sm text-gray-600">Quantity: {item.quantity} units</p>
                        <p className="text-sm font-medium text-[#8FC043]">
                          Tsh {item.unit_price.toLocaleString()} × {item.quantity}
                        </p>
                      </div>
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <p className="text-xl font-bold text-[#EA5829]">
                            Tsh {item.total_price.toLocaleString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(item.product_id)}
                          data-testid={`remove-item-${item.product_id}`}
                        >
                          <Trash2 size={18} className="text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex justify-between text-lg">
                    <span>Subtotal:</span>
                    <span className="font-semibold">Tsh {calculateTotal().toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-2xl font-bold border-t pt-3">
                    <span>Total:</span>
                    <span className="text-[#EA5829]" data-testid="cart-total">
                      Tsh {calculateTotal().toLocaleString()}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/customer/checkout')}
                  className="w-full mt-6"
                  size="lg"
                  data-testid="proceed-checkout-button"
                >
                  Proceed to Checkout
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
